import { Alert } from '@creditornot/cb-alert';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { DragEndEvent } from '@dnd-kit/core';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Image } from 'apis/asset-repository-service/types';
import { ConfirmModal } from 'common/components';
import { ListingManagerErrorPage } from 'modules/shared-menu/components/ListingManagerErrorPage';

import useSharedMenuEditorLists from '../../../SharedMenuEditorPage/hooks/useSharedMenuEditorLists';
import { Offer } from '../../../SharedMenuEditorPage/types';
import { IMAGE_BASE_URL, MAX_OFFER_IMAGES_COUNT } from '../../constants';
import { useListingManagerImageGallery } from '../../hooks/useListingManagerImageGallery';
import { DraftUpdateParams, OfferDraftChanges } from '../../useOfferDraftChanges';
import { getAllOfferImages } from '../../utils';
import { CenteredEmpty } from '../CenteredEmpty';
import { DraggableImageCard } from '../DraggableImageCard';
import { ImageGalleryDndContext } from '../ImageGalleryDndContext';
import { ImageGridLoader } from '../ImageGridLoader';
import { ImagesUploader } from '../ImagesUploader/ImagesUploader';
import { OffersFilters } from '../OffersFilters';
import { OffersListView } from '../OffersListView/OffersListView';

const PAGE_SIZE = 12;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Content = styled.div`
  display: flex;
  height: 100%;
  overflow: hidden;
`;

const ImagesUploaderContainer = styled.div`
  margin-block-end: 16px;
`;

const ImagesPanel = styled.div`
  overflow: auto;
  flex: 1;
  padding: 20px;
`;

const MenuPanel = styled.div`
  width: 500px;
  flex-shrink: 0;
  border-inline-start: 1px solid ${color.border};
  background-color: ${color.bgSurfaceSecondary};
`;

const AlertContainer = styled.div`
  position: sticky;
  background-color: white;
  border-radius: 8px;
  bottom: 0;
`;

type Props = {
  onClose: () => void;
  offersIds?: string[];
  updateOfferDraftChanges: (offerId: string, update: DraftUpdateParams) => void;
  offerDraftChanges: OfferDraftChanges;
  offersWithDraftChanges: Offer[];
  headerContent: React.ReactNode;
  selectedImages?: Image[];
  showUploader?: boolean;
  showInfobox?: boolean;
};

export const ImageGalleryAddToOffers = ({
  onClose,
  offersIds,
  updateOfferDraftChanges,
  offerDraftChanges,
  offersWithDraftChanges,
  headerContent,
  selectedImages,
  showUploader,
  showInfobox,
}: Props) => {
  const intl = useIntl();
  const [isConfirmDiscardChangesModalOpen, setIsConfirmDiscardChangesModalOpen] = useState(false);
  const [alertVisible, setAlertVisible] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  const { imageAssets, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, isSuccess, error } =
    useListingManagerImageGallery({
      size: PAGE_SIZE,
    });
  const { menuEditorList, searchFilter, setSearchFilter, setAppliedFilters } = useSharedMenuEditorLists(
    selectedCategoryId ? 'items-by-category' : 'all-items',
    {
      categoryId: selectedCategoryId,
    },
  );

  const isAnyImageSelected = useMemo(() => selectedImages && selectedImages.length > 0, [selectedImages]);
  const images = useMemo(() => {
    return selectedImages && selectedImages.length > 0 ? selectedImages : imageAssets;
  }, [selectedImages, imageAssets]);
  const allOfferImagesSet = useMemo(() => new Set(getAllOfferImages(offersWithDraftChanges)), [offersWithDraftChanges]);

  const filterMenuEditorList = useCallback(() => {
    if (!offersIds) {
      return menuEditorList;
    }
    const listWithoutNotSelectedOffers = menuEditorList.filter(element => {
      if (element.type === 'item') {
        return offersIds.includes(element.itemState.id);
      }

      return true;
    });

    const listWithoutEmptyCategoriesElements = listWithoutNotSelectedOffers.filter((element, index, arr) => {
      if (element.type === 'item') {
        return true;
      } else if (arr.length === index + 1 || arr[index + 1].type === 'item') {
        return true;
      }

      return false;
    });

    return listWithoutEmptyCategoriesElements;
  }, [menuEditorList, offersIds]);

  const attachImageToOffer = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!active || !over || !updateOfferDraftChanges) {
        return;
      }

      const image = active.data.current?.image;
      const offerId = over.data.current?.offerId;

      updateOfferDraftChanges(offerId, (offer, draft) => {
        const images = draft?.images || offer.images || [];

        if (images.length >= MAX_OFFER_IMAGES_COUNT) {
          return {};
        }

        return {
          images: [...images, image],
        };
      });
    },
    [updateOfferDraftChanges],
  );

  const detachImageFromOffer = useCallback(
    (offerId: string, deleteAt: number) => {
      updateOfferDraftChanges(offerId, (offer, draft) => {
        const images = (draft?.images || offer.images || []).filter((_, index) => index !== deleteAt);

        const areImagesChanged = images.map(({ url }) => url).join() !== offer.images?.map(({ url }) => url).join();

        if (!areImagesChanged) {
          return undefined;
        }

        return {
          image: images[0]?.url,
          images,
        };
      });

      return Promise.resolve();
    },
    [updateOfferDraftChanges],
  );

  return (
    <Root>
      {headerContent}
      <Content>
        <ImageGalleryDndContext onDragEnd={attachImageToOffer}>
          <ImagesPanel>
            {showUploader && (
              <ImagesUploaderContainer>
                <ImagesUploader />
              </ImagesUploaderContainer>
            )}
            {error ? (
              <ListingManagerErrorPage error={error} />
            ) : isSuccess && imageAssets.length === 0 ? (
              <CenteredEmpty
                description={<FormattedMessage id="shared-menu.editor.image-gallery.empty-state.upload-images" />}
                title={intl.formatMessage({ id: 'shared-menu.editor.image-gallery.empty-state.no-images-found' })}
              />
            ) : (
              <ImageGridLoader
                fetchNextPage={fetchNextPage}
                isLoading={isLoading || isFetchingNextPage}
                hasNextPage={isAnyImageSelected ? false : hasNextPage}
                pageSize={PAGE_SIZE}
              >
                {images.map(({ name, id, blurhash, originalAssetId }) => (
                  <DraggableImageCard
                    key={id}
                    enhanced={Boolean(originalAssetId)}
                    attachedToOffer={allOfferImagesSet.has(IMAGE_BASE_URL + id)}
                    blurhash={blurhash}
                    name={name}
                    src={IMAGE_BASE_URL + id}
                  />
                ))}
              </ImageGridLoader>
            )}
            {showInfobox && alertVisible && (
              <AlertContainer>
                <Alert
                  variant="info"
                  title={intl.formatMessage({ id: 'shared-menu.editor.image-gallery.add-to-items.infobox.title' })}
                  style={{ backgroundColor: color.bgSurface }}
                  onCloseButtonClick={() => setAlertVisible(false)}
                >
                  <FormattedMessage id="shared-menu.editor.image-gallery.add-to-items.infobox.description" />
                </Alert>
              </AlertContainer>
            )}
          </ImagesPanel>
          <MenuPanel>
            <OffersFilters
              searchFilter={searchFilter}
              setSearchFilter={setSearchFilter}
              setAppliedFilters={setAppliedFilters}
              selectedCategoryId={selectedCategoryId}
              setSelectedCategoryId={setSelectedCategoryId}
            />
            <OffersListView
              menuEditorList={filterMenuEditorList()}
              offerDraftChanges={offerDraftChanges}
              onDetachOfferImage={detachImageFromOffer}
            />
          </MenuPanel>
        </ImageGalleryDndContext>
        <ConfirmModal
          open={isConfirmDiscardChangesModalOpen}
          header={<FormattedMessage id="dropdown.areYouSure" />}
          description={<FormattedMessage id="shared-menu.editor.image-gallery.confirm-discard-changes-info" />}
          cancelText={<FormattedMessage id="state.cancel" />}
          confirmText={<FormattedMessage id="state.confirm" />}
          onCancel={() => setIsConfirmDiscardChangesModalOpen(false)}
          onConfirm={onClose}
        />
      </Content>
    </Root>
  );
};
